import { useMemo, useCallback, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuctionCategories, useActiveAuctions } from "@/hooks/useAuctions";
import { useAllPlayerNames } from "@/hooks/usePlayerName";
import { useIsMobile } from "@/hooks/use-mobile";
import type { AuctionItem } from "@/types/auction";
import type { SortOption } from "@/components/SortSelect";

// Desktop Components
import StatsCards from "@/components/home/StatsCards";
import SearchBar from "@/components/home/SearchBar";
import CategoryTabs from "@/components/home/CategoryTabs";
import SortSelect from "@/components/SortSelect";
import AuctionGrid from "@/components/AuctionGrid";

// Mobile Components
import MobileHeader from "@/components/home/MobileHeader";
import MobileStatsRow from "@/components/home/MobileStatsRow";
import MobileFilterFAB from "@/components/home/MobileFilterFAB";
import MobileBottomNav from "@/components/layout/MobileBottomNav";

const sortAuctions = (auctions: AuctionItem[], sortOption: SortOption): AuctionItem[] => {
  return [...auctions].sort((a, b) => {
    switch (sortOption) {
      case "price-asc":
        return a.currentBid - b.currentBid;
      case "price-desc":
        return b.currentBid - a.currentBid;
      case "time-asc":
        return new Date(a.endTime).getTime() - new Date(b.endTime).getTime();
      case "time-desc":
        return new Date(b.endTime).getTime() - new Date(a.endTime).getTime();
      case "name-asc":
        return (a.item.displayName || a.item.material).localeCompare(b.item.displayName || b.item.material);
      case "name-desc":
        return (b.item.displayName || b.item.material).localeCompare(a.item.displayName || a.item.material);
      default:
        return 0;
    }
  });
};

const ITEMS_PER_PAGE = 20;
const FILTER_STORAGE_KEY = "auction-filters";

type SearchMode = "item" | "player";

const getStoredFilters = () => {
  try {
    const stored = sessionStorage.getItem(FILTER_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as {
        category: string | null;
        sort: SortOption;
        search: string;
        searchMode: SearchMode;
      };
    }
  } catch {}
  return null;
};

const Index = () => {
  const isMobile = useIsMobile();
  const storedFilters = getStoredFilters();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(storedFilters?.category ?? null);
  const [sortOption, setSortOption] = useState<SortOption>(storedFilters?.sort ?? "time-asc");
  const [searchQuery, setSearchQuery] = useState(storedFilters?.search ?? "");
  const [searchMode, setSearchMode] = useState<SearchMode>(storedFilters?.searchMode ?? "item");
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Persist filters to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({
      category: selectedCategory,
      sort: sortOption,
      search: searchQuery,
      searchMode,
    }));
  }, [selectedCategory, sortOption, searchQuery, searchMode]);
  
  const { data: categories = [], isLoading: categoriesLoading } = useAuctionCategories();
  const { data: auctions = [], isLoading: auctionsLoading, error, refetch, isFetching } = useActiveAuctions(selectedCategory ?? undefined);
  
  // Get all seller UUIDs for player name search
  const allSellerUuids = useMemo(() => [...new Set(auctions.map(a => a.seller))], [auctions]);
  const { data: playerNamesMap = {} } = useAllPlayerNames(allSellerUuids, searchMode === "player" && searchQuery.trim().length > 0);
  
  // Filter by search query
  const filteredAuctions = useMemo(() => {
    if (!searchQuery.trim()) return auctions;
    const query = searchQuery.toLowerCase().trim();
    
    if (searchMode === "item") {
      return auctions.filter(auction => {
        const itemName = (auction.item.displayName || auction.item.material).toLowerCase();
        return itemName.includes(query);
      });
    } else {
      return auctions.filter(auction => {
        const playerName = playerNamesMap[auction.seller];
        return playerName?.toLowerCase().includes(query);
      });
    }
  }, [auctions, searchQuery, searchMode, playerNamesMap]);
  
  const sortedAuctions = useMemo(() => sortAuctions(filteredAuctions, sortOption), [filteredAuctions, sortOption]);
  const totalValue = useMemo(() => filteredAuctions.reduce((sum, a) => sum + a.currentBid, 0), [filteredAuctions]);
  
  // Paginated auctions
  const paginatedAuctions = useMemo(() => sortedAuctions.slice(0, visibleCount), [sortedAuctions, visibleCount]);
  const hasMore = visibleCount < sortedAuctions.length;
  const isInitialLoading = auctionsLoading;
  
  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);
    setIsLoadingMore(false);
  }, [selectedCategory, searchQuery, sortOption]);
  
  // Infinite scroll
  const loadMoreRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasMore || isInitialLoading) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          setIsLoadingMore(true);
          setTimeout(() => {
            setVisibleCount(prev => prev + ITEMS_PER_PAGE);
            setIsLoadingMore(false);
          }, 150);
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );
    
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, isInitialLoading, isLoadingMore]);
  
  const handleCategoryChange = useCallback((category: string | null) => {
    setSelectedCategory(category);
  }, []);
  
  const handleSortChange = useCallback((value: SortOption) => {
    setSortOption(value);
  }, []);
  
  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleSearchModeChange = useCallback((mode: SearchMode) => {
    setSearchMode(mode);
  }, []);

  // Calculate active filters count for mobile FAB badge
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (selectedCategory) count++;
    if (searchQuery.trim()) count++;
    if (sortOption !== "time-asc") count++;
    return count;
  }, [selectedCategory, searchQuery, sortOption]);

  // Mobile Layout
  if (isMobile) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <MobileHeader onRefresh={handleRefresh} isRefreshing={isFetching} />
        
        <MobileStatsRow 
          auctions={auctions} 
          filteredCount={filteredAuctions.length} 
          totalValue={totalValue} 
        />
        
        <main className="flex-1 px-3 pb-24">
          <AuctionGrid 
            auctions={paginatedAuctions}
            allAuctions={sortedAuctions}
            isLoading={isInitialLoading}
            isLoadingMore={isLoadingMore}
            error={error}
            hasMore={hasMore}
            totalCount={sortedAuctions.length}
            loadMoreRef={loadMoreRef}
          />
        </main>

        <MobileFilterFAB
          categories={categories}
          selectedCategory={selectedCategory}
          onSelectCategory={handleCategoryChange}
          sortOption={sortOption}
          onSortChange={handleSortChange}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          searchMode={searchMode}
          onSearchModeChange={handleSearchModeChange}
          activeFiltersCount={activeFiltersCount}
        />

        <MobileBottomNav />
      </div>
    );
  }

  // Desktop Layout - Dashboard Style
  return (
    <>
      {/* Stats Cards */}
      <section className="mb-6 opacity-0 animate-fade-in">
        <StatsCards 
          auctions={auctions} 
          filteredCount={filteredAuctions.length} 
          totalValue={totalValue} 
        />
      </section>

      {/* Filter Toolbar */}
      <section className="mb-6 opacity-0 animate-fade-in" style={{ animationDelay: "0.1s" }}>
        <div className="bg-secondary/20 rounded-xl border border-border/30 p-4">
          {/* Top Row: Search + Refresh */}
          <div className="flex items-center gap-3 mb-4">
            <SearchBar
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              searchMode={searchMode}
              onModeChange={handleSearchModeChange}
              onRefresh={handleRefresh}
              isRefreshing={isFetching}
              className="flex-1"
            />
          </div>
          
          {/* Bottom Row: Categories + Sort */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 overflow-hidden">
              {!categoriesLoading && (
                <CategoryTabs
                  categories={categories}
                  selectedCategory={selectedCategory}
                  onSelectCategory={handleCategoryChange}
                />
              )}
            </div>
            <SortSelect value={sortOption} onChange={handleSortChange} />
          </div>
        </div>
      </section>

      {/* Auction Grid */}
      <section className="opacity-0 animate-fade-in" style={{ animationDelay: "0.2s" }}>
        <AuctionGrid 
          auctions={paginatedAuctions}
          allAuctions={sortedAuctions}
          isLoading={isInitialLoading}
          isLoadingMore={isLoadingMore}
          error={error}
          hasMore={hasMore}
          totalCount={sortedAuctions.length}
          loadMoreRef={loadMoreRef}
        />
      </section>
    </>
  );
};

export default Index;
